import React, { useEffect, useMemo, useState } from "react";
import { notifyError } from "../../utils/tostfy";
import { Link, useOutletContext, useSearchParams } from "react-router-dom";
import { handleApiCall } from "../../apis";
import { endpoint } from "../../utils/endpoints";
import { formatDateTime } from "../../utils/formatDateTime";
import { truncateAmountParts } from "../../utils/formatAmount";
import {
  buildFuelFormImageMeta,
  FuelImageMetadataOverlay,
  FuelImageMetadataPanel,
} from "../../Components/FuelImageMetadata/FuelImageMetadata";

/** Truncate (no rounding) to exactly 2 decimals for KM display. */
const formatKm = (value, suffix = " KM") => {
  const parts = truncateAmountParts(value);
  if (!parts) return "-";
  return `${parts.sign}${parts.intPart}.${parts.fraction}${suffix}`;
};
const FUEL_FORM_STATUS_LABELS = {
  0: "Pending",
  1: "Started",
  2: "Locked",
  3: "Completed",
  4: "Completed",
};

const pickFuelFormStatus = (d) =>
  d?.status ?? d?.fuel_form_status ?? d?.form_status ?? d?.fuel_status;

const toTitleStatus = (status) => {
  if (status == null || status === "") return "-";

  if (typeof status === "string") {
    const s = status.trim();
    const byName = {
      pending: "Pending",
      started: "Started",
      locked: "Locked",
      completed: "Completed",
      active: "Active",
      inactive: "Inactive",
    };
    const named = byName[s.toLowerCase()];
    if (named) return named;
    const n = Number(s);
    if (!Number.isNaN(n) && FUEL_FORM_STATUS_LABELS[n] != null) {
      return FUEL_FORM_STATUS_LABELS[n];
    }
    return s;
  }

  const n = Number(status);
  if (!Number.isNaN(n) && FUEL_FORM_STATUS_LABELS[n] != null) {
    return FUEL_FORM_STATUS_LABELS[n];
  }
  if (typeof status === "number") return String(status);
  return "-";
};

/** API may return the form at root or under a single key */
const unwrapFuelFormPayload = (raw) => {
  if (!raw || typeof raw !== "object") return raw;
  return (
    raw.fuel_form_data ||
    raw.fuel_form ||
    raw.fuelForm ||
    raw.form_data ||
    raw.data ||
    raw
  );
};

const getAssetBaseUrl = () => {
  const api = process.env.REACT_APP_API || "";
  try {
    const u = new URL(api);
    return `${u.protocol}//${u.host}`;
  } catch {
    return "";
  }
};

const resolveImageUrl = (path) => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  const base = getAssetBaseUrl();
  if (!base) return path;
  return `${base}${path.startsWith("/") ? path : `/${path}`}`;
};

const mapFuelDetail = (d) => {
  if (!d || typeof d !== "object") return null;
  const imageMeta = buildFuelFormImageMeta(d, formatDateTime);
  return {
    date: formatDateTime(d.createdAt, "-"),
    vehicle: d?.vehicle_id?.registration_number || "-",
    chassis: d?.vehicle_id?.chassis_number || "-",
    fuelFilled: d?.fuel_quantity != null ? `${d.fuel_quantity} L` : "-",
    fuelOperatorName: d?.fuel_operator_id?.full_name || "-",
    fuelOperatorEmployeeId: d?.fuel_operator_id?.employee_id || "-",
    miplLocation:
      d?.mipl_location_id?.name ||
      d?.fuel_pump_id?.mipl_location_id?.name ||
      d?.mipl_location_name ||
      d?.location_name ||
      "-",
    fuelPumpName: d?.fuel_pump_id?.fuel_pump_name || d?.fuel_pump_name || "-",
    odometerReading: d?.odometer_reading != null ? `${d.odometer_reading} KM` : "-",
    efficiency: d?.efficency != null ? Number(d.efficency).toFixed(2) : "-",
    latitude: imageMeta.latitude || "-",
    longitude: imageMeta.longitude || "-",
    address: imageMeta.address || "-",
    distanceKm: d?.distance_travelled != null ? formatKm(d.distance_travelled) : "-",
    status: toTitleStatus(pickFuelFormStatus(d)),
    images: [
      { label: "Odometer Reading Image", src: resolveImageUrl(d?.odometer_reading_image), ...imageMeta },
      { label: "Truck Front Image", src: resolveImageUrl(d?.vehicle_front_image), ...imageMeta },
      { label: "Before Fueling Image", src: resolveImageUrl(d?.before_fueling_image), ...imageMeta },
      { label: "After Fueling Image", src: resolveImageUrl(d?.after_fueling_image), ...imageMeta },
    ],
  };
};

const FuelDetails = () => {
  const { setSubheading } = useOutletContext();
  const [searchParams] = useSearchParams();
  const fuelFormId = (
    searchParams.get("fuel_form_id") ||
    searchParams.get("fuel_fill_id") ||
    ""
  ).trim();

  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [modalImageWidth, setModalImageWidth] = useState(null);

  useEffect(() => {
    setSubheading("View Details");
    return () => {
      setSubheading("");
    };
  }, [setSubheading]);

  useEffect(() => {
    const load = async () => {
      if (!fuelFormId) {
        setLoading(false);
        setDetail(null);
        return;
      }
      setLoading(true);
      try {
        const response = await handleApiCall(
          "get",
          `${endpoint.viewFuelHistory}?fuel_form_id=${encodeURIComponent(fuelFormId)}`,
          {}
        );
        if (response?.data?.code === 200) {
          const raw = unwrapFuelFormPayload(response?.data?.data);
          setDetail(mapFuelDetail(raw));
        } else {
          const errMsg =
            response?.response?.data?.errorMessage ||
            response?.response?.data?.message ||
            response?.data?.message ||
            "Failed to load fuel history details";
          notifyError(errMsg);
          setDetail(null);
        }
      } catch {
        notifyError("Failed to load fuel history details");
        setDetail(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [fuelFormId]);

  const imageData = useMemo(
    () => (detail?.images || []).filter((img) => Boolean(img.src)),
    [detail]
  );

  const Card = ({ children })       => (
    <div className="bg-white rounded-2xl border px-3 py-2 md:p-6 mb-4 font-UbuntuMedium max-[767px]:text-sm">
      {children}
    </div>
  );

  const Row = ({ label, value }) => (
    <div className="flex justify-between gap-3 py-1">
      <span className="text-gray-400">{label}</span>
      <span className="text-gray-800 text-right break-words">{value || "-"}</span>
    </div>
  );

  if (!fuelFormId && !loading) {
    return (
      <>
        <div className="mt-7">
          <p className="text-gray-500 mb-3">Missing fuel form id. Open details from the list.</p>
          <Link to="/fuel-filled-history" className="btn btn-primary">
            Back
          </Link>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="md:mt-7 mt-3">
        <div className="flex items-center mb-3">
          <h3 className="md:text-xl font-UbuntuMedium my-0">Fuel Details</h3>
          <Link to="/fuel-filled-history" className="btn btn-primary ml-auto">
            Back
          </Link>
        </div>

        {loading ? (
          <div className="bg-white shadow-sm rounded-md p-6 mb-4 text-center text-gray-500">
            Loading...
          </div>
        ) : detail ? (
          <>
            <div className="bg-white md:shadow-sm md:rounded-md md:p-6 mb-4">
              <Card>
                <div className="grid grid-cols-1 md:grid-cols-2 md:gap-12 gap-0">
                  <div>
                    <Row label="Date & Time" value={detail.date} />
                    <Row label="Vehicle" value={detail.vehicle} />
                    {/* <Row label="Chassis No." value={detail.chassis} />
                    <Row label="Fuel Pump Name" value={detail.fuelPumpName} /> */}
                  </div>
                  <div>
                    <Row label="Fuel Filled" value={detail.fuelFilled} />
                    <Row label="Fuel Operator Name" value={detail.fuelOperatorName} />
                    <Row label="Fuel Operator Employee ID" value={detail.fuelOperatorEmployeeId} />
                    <Row label="MIPL Location" value={detail.miplLocation} />
                    {/* <Row label="Status" value={detail.status} /> */}
                  </div>
                </div>
              </Card>
              <Card>
                <div className="grid grid-cols-1 md:grid-cols-2 md:gap-12 gap-0">
                  <div>
                    <Row label="Odometer Reading" value={detail.odometerReading} />
                    <Row label="Distance Travel (KM)" value={detail.distanceKm} />
                    {/* <Row label="Status" value={detail.status} /> */}
                    {/* <Row label="Fuel Efficiency" value={detail.efficiency} />
                    <Row label="Address" value={detail.address} /> */}
                  </div>
                  <div>
                  <Row label="Fuel pump meter before fueling" value={"0"} />
                  <Row label="Fuel pump meter after fueling" value={detail.fuelFilled} />
                  <Row label="Fuel Efficiency" value={detail.efficiency + " km/l"} />
                  
                  </div>
                </div>
              </Card>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5 max-w-screen-xl">
              {imageData.length === 0 ? (
                <div className="sm:col-span-2 xl:col-span-4 text-gray-500 text-sm">No images available</div>
              ) : (
                imageData.map((item) => (
                  <div
                    key={item.label}
                    onClick={() => {
                      setModalImageWidth(null);
                      setSelectedImage(item);
                    }}
                    className="cursor-pointer"
                  >
                    <div className="group relative h-52 overflow-hidden rounded-2xl bg-gray-200 shadow-sm ring-1 ring-black/5 transition hover:shadow-md">
                      <img
                        src={item.src}
                        alt={item.label}
                        className="h-full w-full object-cover transition duration-200 group-hover:scale-[1.02]"
                      />
                      <FuelImageMetadataOverlay
                        capturedAt={item.capturedAt}
                        latitude={item.latitude}
                        longitude={item.longitude}
                        address={item.address}
                      />
                    </div>
                    <p className="mt-2 text-center text-base font-UbuntuMedium text-gray-800">{item.label}</p>
                  </div>
                ))
              )}
            </div>
          </>
        ) : (
          <div className="bg-white shadow-sm rounded-md p-6 mb-4 text-center text-gray-500">
            Could not load fuel history details.
          </div>
        )}

        {selectedImage && (
          <div className="fixed inset-0 bg-black/70 flex justify-center items-center z-[9999] p-4">
            <div
              className="inline-flex max-h-[90vh] max-w-[92vw] flex-col overflow-hidden rounded-xl bg-transparent shadow-2xl"
              style={modalImageWidth ? { width: `${modalImageWidth}px` } : undefined}
            >
              <div className="relative bg-transparent">
                <button
                  onClick={() => {
                    setSelectedImage(null);
                    setModalImageWidth(null);
                  }}
                  className="absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full border border-white/70 bg-white/95 text-xl font-bold leading-none text-gray-800 shadow-md transition hover:bg-white"
                  type="button"
                  aria-label="Close image preview"
                >
                  ×
                </button>

                <img
                  src={selectedImage.src}
                  alt={selectedImage.label || "Full Preview"}
                  className="block max-h-[72vh] max-w-[92vw] rounded-t-xl object-contain"
                  onLoad={(event) => {
                    setModalImageWidth(Math.ceil(event.currentTarget.getBoundingClientRect().width));
                  }}
                />
              </div>
              <FuelImageMetadataPanel
                capturedAt={selectedImage.capturedAt}
                latitude={selectedImage.latitude}
                longitude={selectedImage.longitude}
                address={selectedImage.address}
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default FuelDetails;
